import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import {
  SEARCH_PARAM_VALUE_KEY,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { useAutoSuggestedCompanies } from '@aibees/elephant/hooks/company/use-auto-suggested-companies';
import { ElephantPresetType, SearchType } from '@aibees/elephant/types';
import { useLookalikeSeedsStore } from '@aibees/elephant/store/lookalike-seeds';
import { sanitizeLinkedinUrl } from '@aibees/elephant/utils/url';
import { classnames } from '@aibees/shared/utils';
import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SuggestionSearchBarOptionBase } from '../../feature/suggestion-search-bar';
import { useLookalikeSeeds } from './hooks/use-lookalike-seeds';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanySimilaritySearchProps as Props } from './types';
import { Icon } from '@aibees/shared/components';

// TODO @Mohammad79-nkv please check the error eslint-disable-next-line react-hooks/rules-of-hooks

export const CompanySimilaritySearch = memo(
  ({ className, children, compact, renderMode, dialogState }: Props) => {
    const { track } = useTrackEvents();
    const { setParams, params, getParam, removeParam } = useSearchQueryFilter();
    const navigate = useNavigate();
    const type = useSearchTypeFromRoute();
    const searchKey = SEARCH_PARAM_VALUE_KEY;
    const defaultValues = useLookalikeSeeds();
    const mergeSeeds = useLookalikeSeedsStore((state) => state.mergeSeeds);

    const handleSearch = (values: SuggestionSearchBarOptionBase[]) => {
      const searchValues: string[] = values.map((i) => i.value);

      // Cache the picked chips' metadata so the results page renders them
      // instantly — no browse round-trip and no loading flash on the chips.
      mergeSeeds(String(SearchType.Company), values);

      if (type !== SearchType.Company) {
        const queryString = new URLSearchParams({
          [searchKey]: searchValues.join(FILTER_VALUE_SEPARATOR),
        }).toString();
        navigate(`/search/company?${queryString}`, { replace: true });
        dialogState?.onFalse();
        return;
      }

      setParams({
        ...params,
        ...{ [searchKey]: searchValues.join(FILTER_VALUE_SEPARATOR) },
      });

      if (type)
        track(type, {
          input: searchValues,
        });

      dialogState?.onFalse();
    };

    const handleDelete = (
      linkedinUrl: string,
      values: SuggestionSearchBarOptionBase[]
    ) => {
      const value = getParam('value');
      if (value) {
        const draft = values.filter((i) => i.value !== linkedinUrl);
        handleSearch(draft);
        if (!draft.length) {
          removeParam(String(searchKey));
        }
      }
    };

    const dialogMode = renderMode === 'dialog';

    const searchButton = useMemo(
      () => (
        <Styled.SearchButton
          variant="contained"
          color="primary"
          startIcon={<Icon name="outline-auto-awesome" size={16} />}
          size="medium"
          className={classnames({ dialogMode })}
        >
          Search
        </Styled.SearchButton>
      ),
      [dialogMode]
    );

    return (
      <Styled.Wrapper
        className={classnames('CompanySimilaritySearch', className)}
        sx={SX.root}
      >
        <Styled.SearchBar
          renderMode={renderMode}
          searchCategory="company"
          className={classnames({ compact })}
          multiple
          limit={5}
          searchIcon="business"
          limitErrorMsg="Max 5 lookalikes has been reached."
          recentPresetType={ElephantPresetType.COMPANY_SIMILARITY_RECENT_SEARCH}
          placeholder="e.g. Nike, nike.com, linkedin.com/company/nike"
          searchButton={searchButton}
          onClickSearchButton={handleSearch}
          fetchOptions={(searchValue: string) =>
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useAutoSuggestedCompanies(sanitizeLinkedinUrl(searchValue))
          }
          defaultValue={defaultValues}
          onDelete={(i, searchValue) => handleDelete(i.value!, searchValue)}
          deleteItemWithBackspace
        >
          {children}
        </Styled.SearchBar>
      </Styled.Wrapper>
    );
  }
);

CompanySimilaritySearch.displayName = 'CompanySimilaritySearch';
